import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  competitionBadge,
  competitionStatusKey,
  needsConfirmation,
  trophyBadge,
  trophyStatusKey,
} from '../../utils/lifecycle-status';
import type { CompetitionStatus, LifecycleStatus } from '../../types/competition';
import type { TrophyStatus } from '../../types/trophy';

interface StatusCardProps {
  /** Which life cycle this is: a circuit has four statuses, an event six. */
  kind: 'trophy' | 'competition';
  status: LifecycleStatus;
  /**
   * Where it may go from here, as the server's machine answered
   * (`available_statuses`). Doors that would actually open: a circuit with
   * entrants simply does not list "draft".
   */
  available: LifecycleStatus[];
  /** A competition only: false once the hour its organiser set has passed. */
  entriesOpen?: boolean;
  saving?: boolean;
  onChange: (status: LifecycleStatus) => void;
}

/**
 * Where this place is in its life, and the moves available from here.
 *
 * Buttons rather than a select, because there are never more than five and
 * each one is a decision worth reading before it is taken. The ones that
 * change who can see the place — and, for a circuit, the one that closes every
 * stage with it — ask first, naming what happens; the rest apply on the tap,
 * which is what running an event on the day needs.
 *
 * The moves themselves are the server's answer, not a copy of its machine:
 * `available_statuses` comes off the record (Location#available_statuses), so a
 * door that would be refused is never drawn in the first place.
 */
export default function StatusCard({
  kind,
  status,
  available,
  entriesOpen,
  saving,
  onChange,
}: StatusCardProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<LifecycleStatus | null>(null);

  const isTrophy = kind === 'trophy';
  const badge = isTrophy
    ? trophyBadge(status as TrophyStatus)
    : competitionBadge(status as CompetitionStatus, entriesOpen);
  const labelKey = (value: LifecycleStatus) =>
    isTrophy ? trophyStatusKey(value as TrophyStatus) : competitionStatusKey(value as CompetitionStatus);

  const request = (target: LifecycleStatus) => {
    if (needsConfirmation(status, target)) {
      setPending(target);
      return;
    }
    onChange(target);
  };

  const confirm = () => {
    if (!pending) return;
    const target = pending;
    setPending(null);
    onChange(target);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.lifecycle.title')}</CardTitle>
          <CardDescription>
            {t(isTrophy ? 'gymAdmin.lifecycle.trophyDescription' : 'gymAdmin.lifecycle.competitionDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('gymAdmin.lifecycle.current')}</span>
            <Badge variant={badge.variant} data-testid="lifecycle-current">
              {t(badge.labelKey)}
            </Badge>
            {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">{t('gymAdmin.lifecycle.moveTo')}</p>
            <div className="flex flex-wrap gap-2">
              {available.map((target) => (
                <Button
                  key={target}
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  onClick={() => request(target)}
                  data-testid={`lifecycle-move-${target}`}
                >
                  {t(labelKey(target))}
                </Button>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {t(`gymAdmin.lifecycle.hint.${isTrophy ? 'trophy' : 'competition'}.${status}`, { defaultValue: '' })}
            </p>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('gymAdmin.lifecycle.confirmTitle', {
                status: pending ? t(labelKey(pending)) : '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending &&
                t(`gymAdmin.lifecycle.confirm.${isTrophy ? 'trophy' : 'competition'}.${pending}`, {
                  defaultValue: t('gymAdmin.lifecycle.confirm.generic'),
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirm} data-testid="lifecycle-confirm">
              {t('gymAdmin.lifecycle.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
